import { 
  FileQuestion, 
  Search, 
  FolderOpen, 
  Users, 
  FileText, 
  Inbox,
  type LucideIcon 
} from 'lucide-react'
import * as React from 'react'

import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

/**
 * 预设图标类型
 */
export type EmptyStatePreset = 
  | 'no-data' 
  | 'no-results' 
  | 'no-items' 
  | 'no-users' 
  | 'no-documents'
  | 'empty-inbox'

/**
 * EmptyState 组件属性
 */
export interface EmptyStateProps {
  /** 预设类型 */
  preset?: EmptyStatePreset
  /** 自定义图标 */
  icon?: LucideIcon
  /** 标题 */
  title?: string
  /** 描述 */
  description?: string
  /** 操作按钮 */
  action?: {
    label: string
    onClick: () => void
  }
  /** 次要操作 */
  secondaryAction?: {
    label: string
    onClick: () => void
  }
  /** 自定义类名 */
  className?: string
  /** 子元素 */
  children?: React.ReactNode
}

/**
 * 预设配置
 */
const presets: Record<EmptyStatePreset, { icon: LucideIcon; title: string; description: string }> = {
  'no-data': {
    icon: Inbox,
    title: '暂无数据',
    description: '当前没有可显示的数据',
  },
  'no-results': {
    icon: Search,
    title: '未找到结果',
    description: '没有找到匹配的结果，请尝试其他搜索条件',
  },
  'no-items': {
    icon: FolderOpen,
    title: '暂无内容',
    description: '还没有添加任何内容',
  },
  'no-users': {
    icon: Users,
    title: '暂无成员',
    description: '还没有添加任何成员',
  },
  'no-documents': {
    icon: FileText,
    title: '暂无文档',
    description: '还没有上传任何文档',
  },
  'empty-inbox': {
    icon: Inbox,
    title: '收件箱为空',
    description: '没有新的消息或通知',
  },
}

/**
 * EmptyState 空状态组件
 * 用于显示列表为空、搜索无结果等场景
 * 
 * @example
 * ```tsx
 * // 使用预设
 * <EmptyState preset="no-results" />
 * 
 * // 自定义内容
 * <EmptyState
 *   icon={FileQuestion}
 *   title="暂无项目"
 *   description="点击下方按钮创建第一个项目"
 *   action={{ label: '创建项目', onClick: handleCreate }}
 * />
 * ```
 */
export function EmptyState({
  preset,
  icon,
  title,
  description,
  action,
  secondaryAction,
  className,
  children,
}: EmptyStateProps) {
  // 获取预设配置
  const presetConfig = preset ? presets[preset] : null
  
  // 合并配置
  const Icon = icon ?? presetConfig?.icon ?? FileQuestion
  const displayTitle = title ?? presetConfig?.title ?? '暂无数据'
  const displayDescription = description ?? presetConfig?.description

  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center py-12 px-4 text-center',
        className
      )}
    >
      {/* 图标 */}
      <div className="mb-4 rounded-full bg-muted p-4">
        <Icon className="h-8 w-8 text-muted-foreground" />
      </div>

      {/* 标题 */}
      <h3 className="text-lg font-medium text-foreground mb-2">
        {displayTitle}
      </h3>

      {/* 描述 */}
      {displayDescription && (
        <p className="text-sm text-muted-foreground max-w-sm mb-6">
          {displayDescription}
        </p>
      )}

      {/* 操作按钮 */}
      {(action || secondaryAction) && (
        <div className="flex gap-3">
          {secondaryAction && (
            <Button variant="outline" onClick={secondaryAction.onClick}>
              {secondaryAction.label}
            </Button>
          )}
          {action && (
            <Button onClick={action.onClick}>
              {action.label}
            </Button>
          )}
        </div>
      )}

      {/* 自定义内容 */}
      {children}
    </div>
  )
}

// ==================== 预设组件 ====================

/**
 * 搜索无结果
 */
export function NoSearchResults({
  query,
  onClear,
}: {
  query?: string
  onClear?: () => void
}) {
  return (
    <EmptyState
      preset="no-results"
      description={
        query
          ? `没有找到与 "${query}" 相关的结果`
          : '没有找到匹配的结果，请尝试其他搜索条件'
      }
      action={onClear ? { label: '清除搜索', onClick: onClear } : undefined}
    />
  )
}

/**
 * 列表为空
 */
export function NoListItems({
  itemName = '数据',
  onCreate,
}: {
  itemName?: string
  onCreate?: () => void
}) {
  return (
    <EmptyState
      preset="no-items"
      title={`暂无${itemName}`}
      description={`还没有添加任何${itemName}`}
      action={onCreate ? { label: `添加${itemName}`, onClick: onCreate } : undefined}
    />
  )
}

/**
 * 筛选无结果
 */
export function NoFilterResults({
  onReset,
}: {
  onReset?: () => void
}) {
  return (
    <EmptyState
      icon={Search}
      title="没有匹配的结果"
      description="当前筛选条件下没有数据，请尝试调整筛选条件"
      action={onReset ? { label: '重置筛选', onClick: onReset } : undefined}
    />
  )
}
